import { Box, HStack } from "@chakra-ui/react";

const NUM_TIMER_BARS = 9;

// Pairs that turn off together (outside to center)
// Step 0: bars 0,8 | Step 1: bars 1,7 | Step 2: bars 2,6 | Step 3: bars 3,5 | Step 4: bar 4
const TURN_OFF_STEPS = [
  [0, 8], // outermost pair
  [1, 7],
  [2, 6],
  [3, 5],
  [4], // center bar
];

export type JeopardyTimerBarProps = {
  secondsRemaining: number;
  totalSeconds: number;
  size?: "sm" | "md" | "lg";
  position?: "absolute" | "relative";
};

const sizeConfig = {
  sm: { barWidth: "20px", barHeight: "12px", spacing: "4px", padding: "8px 12px" },
  md: { barWidth: "30px", barHeight: "18px", spacing: "6px", padding: "10px 16px" },
  lg: { barWidth: "40px", barHeight: "25px", spacing: "8px", padding: "12px 20px" },
};

export const JeopardyTimerBar: React.FC<JeopardyTimerBarProps> = ({
  secondsRemaining,
  totalSeconds,
  size = "lg",
  position = "absolute",
}) => {
  // Calculate how many steps should be off (pairs turn off together)
  const timePerStep = totalSeconds / TURN_OFF_STEPS.length;
  const stepsToTurnOff = Math.floor((totalSeconds - secondsRemaining) / timePerStep);

  // Create set of bar indices that should be off
  const offBars = new Set<number>();
  for (let i = 0; i < stepsToTurnOff; i++) {
    TURN_OFF_STEPS[i].forEach((barIndex) => offBars.add(barIndex));
  }

  const config = sizeConfig[size];

  return (
    <HStack
      position={position}
      top={position === "absolute" ? "30px" : undefined}
      left={position === "absolute" ? "50%" : undefined}
      transform={position === "absolute" ? "translateX(-50%)" : undefined}
      spacing={config.spacing}
      padding={config.padding}
      bg="rgba(30, 30, 30, 0.9)"
      borderRadius="8px"
      border="2px solid"
      borderColor="gray.600"
      zIndex={1001}
    >
      {Array.from({ length: NUM_TIMER_BARS }).map((_, index) => (
        <Box
          key={index}
          width={config.barWidth}
          height={config.barHeight}
          borderRadius="4px"
          bg={offBars.has(index) ? "gray.700" : "red.500"}
          boxShadow={offBars.has(index) ? "none" : "0 0 10px rgba(255, 0, 0, 0.6)"}
          transition="all 0.15s ease-out"
        />
      ))}
    </HStack>
  );
};
